import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../lib/api';
import {
  Plus, X, Save, Edit, Trash2, Target, Calendar,
  Users as UsersIcon, Building2,
} from 'lucide-react';
import { localDateISO } from '../lib/dateUtils';
import { confirmDialog } from '../components/ConfirmDialog';

const ERP = `${API}/erp`;
const inputClass = "w-full px-3 py-2.5 bg-white border-2 border-slate-300 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-violet-600 focus:ring-2 focus:ring-violet-500/20 transition-all";

const fmtMoney = (v) => `R$ ${(Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDateBR = (s) => {
  if (!s) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s.split('-').reverse().join('/');
  try { return new Date(s).toLocaleDateString('pt-BR'); } catch { return s; }
};

// iter77ct.112: input com mask BRL (formata enquanto digita). Digite 5000000 -> R$ 50.000,00
// Aceita edicao de metas ja existentes onde form.value pode vir como number.
const MetaCurrencyInput = ({ value, onChange, dataTestId }) => {
  const numToText = (n) => (Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const [text, setText] = useState(numToText(value));
  useEffect(() => { setText(numToText(value)); /* eslint-disable-next-line */ }, [value]);
  const handle = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) { setText(''); onChange(0); return; }
    const dec = parseInt(raw, 10) / 100;
    setText(dec.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    onChange(dec);
  };
  return (
    <div className="relative w-full">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none font-semibold">R$</span>
      <input
        type="text"
        inputMode="numeric"
        value={text}
        onChange={handle}
        placeholder="0,00"
        className="w-full pl-10 pr-3 py-2.5 bg-white border-2 border-slate-300 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-violet-600 focus:ring-2 focus:ring-violet-500/20 transition-all font-mono font-bold"
        data-testid={dataTestId}
      />
    </div>
  );
};

const todayISO = () => localDateISO();
const addDaysISO = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return localDateISO(d);
};

const emptyForm = () => ({
  name: '',
  value: '',
  type: 'vendas',
  start_date: todayISO(),
  end_date: addDaysISO(30),
  apply_to: 'empresa',
  vendor_ids: [],
  vendor_values: {}, // iter156de: rateio manual por vendedor (apenas quando apply_to === 'ambos')
  notes: '',
  company_id: '',
  active: true,
});

export const MetasPage = ({ companyId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all'); // all | active | inactive
  const [expanded, setExpanded] = useState({}); // id -> bool

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeFilter === 'active') params.append('active', 'true');
      if (activeFilter === 'inactive') params.append('active', 'false');
      if (companyId) params.append('company_id', companyId);
      const [t, e, c] = await Promise.all([
        axios.get(`${ERP}/targets?${params}`, { withCredentials: true }),
        axios.get(`${ERP}/employees${companyId ? `?company_id=${companyId}` : ''}`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
        axios.get(`${ERP}/companies`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
      ]);
      // Hydrate progress for each
      const rows = t.data?.data || [];
      const enriched = await Promise.all(rows.map(async (r) => {
        try {
          const { data } = await axios.get(`${ERP}/targets/${r.id}`, { withCredentials: true });
          return data;
        } catch { return r; }
      }));
      setItems(enriched);
      setEmployees(e.data?.data || []);
      setCompanies(c.data?.data || []);
    } catch (err) {
      toast.error('Erro ao carregar metas');
    } finally {
      setLoading(false);
    }
  }, [activeFilter, companyId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const sellers = useMemo(() => employees.filter(e => !e.type || e.type === 'vendedor'), [employees]);

  const resetForm = () => { setForm(emptyForm()); setEditing(null); };
  const openNew = () => { resetForm(); setShowForm(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({
      name: m.name || '',
      value: m.value || '',
      type: m.type || 'vendas',
      start_date: m.start_date || todayISO(),
      end_date: m.end_date || addDaysISO(30),
      apply_to: m.apply_to || 'empresa',
      vendor_ids: m.vendor_ids || [],
      vendor_values: m.vendor_values || {},
      notes: m.notes || '',
      company_id: m.company_id || '',
      active: m.active !== false,
    });
    setShowForm(true);
  };

  const toggleVendor = (id) => {
    setForm(p => {
      const set = new Set(p.vendor_ids || []);
      if (set.has(id)) set.delete(id); else set.add(id);
      // iter156de: se removeu vendedor, tira ele do vendor_values tambem
      const vv = { ...(p.vendor_values || {}) };
      if (!set.has(id)) delete vv[id];
      return { ...p, vendor_ids: Array.from(set), vendor_values: vv };
    });
  };

  const setVendorValue = (vendorId, val) => {
    setForm(p => ({ ...p, vendor_values: { ...(p.vendor_values || {}), [vendorId]: Number(val) || 0 } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = Number(form.value) || 0;
    if (!form.name.trim()) return toast.error('Informe o nome da meta');
    if (!value || value <= 0) return toast.error('Informe um valor de meta válido');
    if (form.start_date > form.end_date) return toast.error('Data inicial deve ser anterior à final');
    if ((form.apply_to === 'vendedores' || form.apply_to === 'ambos') && form.vendor_ids.length === 0) {
      return toast.error('Selecione pelo menos um vendedor');
    }
    const payload = {
      name: form.name.trim(),
      value: value,
      type: form.type,
      start_date: form.start_date,
      end_date: form.end_date,
      apply_to: form.apply_to,
      vendor_ids: form.vendor_ids,
      // iter156de: envia vendor_values SOMENTE quando apply_to === 'ambos'.
      // Nos demais modos, backend faz split igualitario (comportamento legado).
      vendor_values: form.apply_to === 'ambos'
        ? Object.fromEntries((form.vendor_ids || []).map(vid => [vid, Number((form.vendor_values || {})[vid]) || 0]))
        : null,
      notes: form.notes || '',
      company_id: form.company_id || null,
      active: !!form.active,
    };
    try {
      if (editing) {
        await axios.put(`${ERP}/targets/${editing.id}`, payload, { withCredentials: true });
        toast.success('Meta atualizada');
      } else {
        await axios.post(`${ERP}/targets`, payload, { withCredentials: true });
        toast.success('Meta criada');
      }
      setShowForm(false); resetForm(); await fetchAll();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao salvar meta';
      toast.error(typeof msg === 'string' ? msg : 'Erro ao salvar meta');
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog({ title: 'Excluir esta meta? Esta ação não pode ser desfeita.', confirmLabel: 'Confirmar', danger: true }))) return;
    try {
      await axios.delete(`${ERP}/targets/${id}`, { withCredentials: true });
      toast.success('Meta excluída');
      await fetchAll();
    } catch { toast.error('Erro ao excluir'); }
  };

  const isCurrent = (m) => {
    const today = todayISO();
    return m.active !== false && (m.start_date || '') <= today && (m.end_date || '') >= today;
  };

  const APPLY_LABELS = { empresa: 'Empresa', vendedores: 'Vendedores', ambos: 'Empresa + Vendedores' };

  return (
    <div data-testid="metas-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-black tracking-tight text-slate-900 flex items-center gap-2"><Target size={22} className="text-violet-600" /> Metas</h2>
          <p className="text-xs text-slate-500 mt-0.5">Acompanhe metas de vendas da empresa e dos vendedores.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1">
            {[
              { k: 'all', label: 'Todas' },
              { k: 'active', label: 'Ativas' },
              { k: 'inactive', label: 'Inativas' },
            ].map(t => (
              <button key={t.k} onClick={() => setActiveFilter(t.k)} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${activeFilter === t.k ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-testid={`metas-filter-${t.k}`}>{t.label}</button>
            ))}
          </div>
          <button onClick={openNew} className="btn-pill-primary flex items-center gap-1.5 px-4" data-testid="add-meta-btn"><Plus size={16} /> Nova Meta</button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border-2 border-violet-200 rounded-2xl p-5 mb-5 shadow-sm" data-testid="meta-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-black text-slate-900 text-lg">{editing ? 'Editar Meta' : 'Nova Meta'}</h3>
            <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-600 mb-1">Nome da Meta *</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputClass} placeholder="Ex: Meta Junho 2026" required data-testid="meta-name" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Valor da Meta (R$) *</label>
              {/* iter77ct.112: mask BRL — 5000000 -> R$ 50.000,00 (pontuacao automatica) */}
              <MetaCurrencyInput
                value={form.value}
                onChange={(v) => setForm({ ...form, value: v })}
                dataTestId="meta-value"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Tipo</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className={inputClass} data-testid="meta-type">
                <option value="vendas">Vendas (R$)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Início *</label>
              <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className={inputClass} required data-testid="meta-start" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Fim *</label>
              <input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className={inputClass} required data-testid="meta-end" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-600 mb-1">Aplicar para</label>
              <div className="grid grid-cols-3 gap-2">
                {['empresa', 'vendedores', 'ambos'].map(opt => (
                  <button key={opt} type="button" onClick={() => setForm({ ...form, apply_to: opt })}
                    className={`px-3 py-2 rounded-lg text-xs font-bold border-2 transition-all ${form.apply_to === opt ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-300'}`}
                    data-testid={`meta-applyto-${opt}`}>
                    {APPLY_LABELS[opt]}
                  </button>
                ))}
              </div>
            </div>
            {companies.length > 1 && (
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-600 mb-1">Empresa (opcional — vazio = todas)</label>
                <select value={form.company_id} onChange={e => setForm({ ...form, company_id: e.target.value })} className={inputClass} data-testid="meta-company">
                  <option value="">Todas as filiais</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
                </select>
              </div>
            )}
            {(form.apply_to === 'vendedores' || form.apply_to === 'ambos') && (
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-600 mb-1">Vendedores incluídos ({form.vendor_ids.length})</label>
                <div className="border-2 border-slate-200 rounded-lg p-3 max-h-40 overflow-y-auto flex flex-wrap gap-1.5 bg-slate-50/50">
                  {sellers.length === 0 && <span className="text-xs text-slate-400 italic">Nenhum vendedor cadastrado. Vá em Cadastros &gt; Funcionários.</span>}
                  {sellers.map(s => {
                    const on = form.vendor_ids.includes(s.id);
                    return (
                      <button type="button" key={s.id} onClick={() => toggleVendor(s.id)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${on ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-300'}`}
                        data-testid={`meta-vendor-${s.id}`}>
                        {on && '✓ '}{s.name}
                      </button>
                    );
                  })}
                </div>
                {form.apply_to === 'ambos'
                  ? <p className="text-[10px] text-slate-400 mt-1">Modo Empresa + Vendedores: informe abaixo a meta individual de cada vendedor selecionado.</p>
                  : <p className="text-[10px] text-slate-400 mt-1">Em modo Vendedores, o valor da meta é dividido igualmente entre os selecionados.</p>
                }
              </div>
            )}
            {/* iter156de: rateio manual por vendedor — SOMENTE quando "Empresa + Vendedores" */}
            {form.apply_to === 'ambos' && form.vendor_ids.length > 0 && (
              <div className="md:col-span-2 bg-violet-50/50 border-2 border-violet-200 rounded-xl p-3" data-testid="meta-vendor-values-section">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <label className="text-xs font-black text-violet-800 uppercase tracking-wide">Meta individual por vendedor</label>
                  <span className="text-[10px] text-slate-500">Soma: <b className="text-slate-900">{fmtMoney(Object.values(form.vendor_values || {}).reduce((a, b) => a + (Number(b) || 0), 0))}</b></span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {form.vendor_ids.map(vid => {
                    const seller = sellers.find(s => s.id === vid);
                    const vname = seller?.name || 'Sem nome';
                    return (
                      <div key={vid} className="flex items-center gap-2 bg-white border border-violet-200 rounded-lg p-2" data-testid={`meta-vv-row-${vid}`}>
                        <span className="text-xs font-bold text-slate-700 truncate flex-1 min-w-0" title={vname}>{vname}</span>
                        <div className="w-40 flex-shrink-0">
                          <MetaCurrencyInput
                            value={(form.vendor_values || {})[vid] || 0}
                            onChange={(v) => setVendorValue(vid, v)}
                            dataTestId={`meta-vv-input-${vid}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-500 mt-2">A soma não precisa ser igual ao valor total da meta. Cada vendedor terá seu próprio objetivo.</p>
              </div>
            )}
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-600 mb-1">Observações</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className={inputClass} rows={2} placeholder="Opcional" data-testid="meta-notes" />
            </div>
            <label className="flex items-center gap-2 md:col-span-2">
              <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-violet-600" data-testid="meta-active" />
              <span className="text-xs font-bold text-slate-700">Meta ativa</span>
            </label>
          </div>
          <div className="flex gap-2 mt-5">
            <button type="submit" className="btn-pill-primary flex items-center gap-1.5 px-5" data-testid="save-meta-btn"><Save size={14} /> {editing ? 'Atualizar' : 'Criar'}</button>
            <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="px-4 py-2.5 text-sm text-slate-500 hover:text-slate-700">Cancelar</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-2 border-violet-600 border-t-transparent" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-slate-200">
          <Target size={36} className="mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 font-bold text-sm">Nenhuma meta cadastrada</p>
          <p className="text-slate-400 text-xs mt-1">Clique em &ldquo;Nova Meta&rdquo; para criar sua primeira meta de vendas.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="metas-list">
          {items.map(m => {
            const progress = m.progress || { atual: 0, meta: m.value || 0, percentual: 0, by_vendor: [] };
            const pct = Math.min(100, progress.percentual || 0);
            const pctColor = pct >= 100 ? 'from-emerald-500 to-green-600' : pct >= 70 ? 'from-blue-500 to-violet-600' : pct >= 40 ? 'from-amber-500 to-orange-500' : 'from-rose-500 to-red-600';
            const current = isCurrent(m);
            const isOpen = expanded[m.id];
            return (
              <div key={m.id} className={`bg-white border-2 rounded-2xl overflow-hidden transition-all ${current ? 'border-violet-300 shadow-md shadow-violet-100' : 'border-slate-200'}`} data-testid={`meta-row-${m.id}`}>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-black text-slate-900 text-base truncate">{m.name}</h3>
                        {current && <span className="text-[10px] font-bold text-violet-700 bg-violet-100 px-2 py-0.5 rounded-full">VIGENTE</span>}
                        {m.active === false && <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">INATIVA</span>}
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
                        <span className="flex items-center gap-1"><Calendar size={11} />{fmtDateBR(m.start_date)} → {fmtDateBR(m.end_date)}</span>
                        <span className="flex items-center gap-1">{m.apply_to === 'vendedores' || m.apply_to === 'ambos' ? <UsersIcon size={11} /> : <Building2 size={11} />}{APPLY_LABELS[m.apply_to] || m.apply_to}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => openEdit(m)} className="p-1.5 text-slate-400 hover:text-violet-600" title="Editar" data-testid={`edit-meta-${m.id}`}><Edit size={14} /></button>
                      <button onClick={() => handleDelete(m.id)} className="p-1.5 text-slate-400 hover:text-rose-500" title="Excluir" data-testid={`delete-meta-${m.id}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="text-center bg-slate-50 rounded-lg p-2">
                      <p className="text-[10px] font-bold text-slate-500 uppercase">Meta</p>
                      <p className="text-sm font-black text-slate-900 mt-0.5">{fmtMoney(m.value)}</p>
                    </div>
                    <div className="text-center bg-emerald-50 rounded-lg p-2">
                      <p className="text-[10px] font-bold text-emerald-700 uppercase">Atual</p>
                      <p className="text-sm font-black text-emerald-700 mt-0.5">{fmtMoney(progress.atual)}</p>
                    </div>
                    <div className="text-center bg-violet-50 rounded-lg p-2">
                      <p className="text-[10px] font-bold text-violet-700 uppercase">% Atingido</p>
                      <p className="text-sm font-black text-violet-700 mt-0.5">{(progress.percentual || 0).toFixed(1)}%</p>
                    </div>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                    <div className={`h-full bg-gradient-to-r ${pctColor} transition-all`} style={{ width: `${pct}%` }} data-testid={`meta-progress-${m.id}`} />
                  </div>
                  {progress.by_vendor && progress.by_vendor.length > 0 && (
                    <button type="button" onClick={() => setExpanded(p => ({ ...p, [m.id]: !p[m.id] }))} className="mt-3 text-[11px] text-violet-600 font-bold hover:text-violet-700" data-testid={`meta-expand-${m.id}`}>
                      {isOpen ? '▲ Ocultar' : '▼ Ver'} progresso por vendedor ({progress.by_vendor.length})
                    </button>
                  )}
                  {isOpen && progress.by_vendor && progress.by_vendor.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                      {progress.by_vendor.map(v => {
                        const vpct = Math.min(100, v.percentual || 0);
                        const vColor = vpct >= 100 ? 'from-emerald-500 to-green-600' : vpct >= 70 ? 'from-blue-500 to-violet-600' : vpct >= 40 ? 'from-amber-500 to-orange-500' : 'from-rose-500 to-red-600';
                        return (
                          <div key={v.vendedor_id} className="text-xs" data-testid={`meta-vendor-row-${v.vendedor_id}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-bold text-slate-700 truncate">{v.name || 'Sem nome'}</span>
                              <span className="font-black text-slate-900">{fmtMoney(v.atual)} / {fmtMoney(v.meta || 0)} ({(v.percentual || 0).toFixed(1)}%)</span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                              <div className={`h-full bg-gradient-to-r ${vColor}`} style={{ width: `${vpct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ============================================================
   Dashboard card — current target progress
   ============================================================ */
export const MetaDashboardCard = ({ companyId, referenceDate = null }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetch = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (companyId) params.append('company_id', companyId);
        // iter156p: quando o dashboard esta no chip "Anterior" (mes anterior), o pai passa
        // referenceDate = ultimo dia do mes anterior. Endpoint retorna metas vigentes naquela data.
        if (referenceDate) params.append('reference_date', referenceDate);
        const { data } = await axios.get(`${ERP}/targets/current?${params}`, { withCredentials: true });
        if (alive) setData(data?.data || []);
      } catch { if (alive) setData([]); }
      finally { if (alive) setLoading(false); }
    };
    fetch();
    return () => { alive = false; };
  }, [companyId, referenceDate]);

  if (loading) return null;
  if (!data || data.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-3 mb-4 relative overflow-hidden" data-testid="dash-meta-card">
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-violet-600 to-cyan-500" />
      <div className="space-y-2.5 mt-1">
        {data.map(m => {
          const progress = m.progress || { atual: 0, meta: m.value || 0, percentual: 0, by_vendor: [] };
          const pct = Math.min(100, progress.percentual || 0);
          const pctColor = pct >= 100 ? 'from-emerald-500 to-green-600' : pct >= 70 ? 'from-blue-500 to-violet-600' : pct >= 40 ? 'from-amber-500 to-orange-500' : 'from-rose-500 to-red-600';
          // iter156de: exibe nomes dos vendedores APENAS quando apply_to === 'ambos'
          // E `vendor_values` foi configurado (empresa gerencia meta por vendedor).
          // Nos demais modos, mantem apenas a meta agregada da empresa (comportamento legado).
          const hasVendorValues = m.apply_to === 'ambos'
            && m.vendor_values
            && typeof m.vendor_values === 'object'
            && Object.values(m.vendor_values).some(v => Number(v) > 0);
          return (
            <div key={m.id} data-testid={`dash-meta-${m.id}`}>
              <div className="flex items-center justify-between mb-1 gap-2">
                <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                  <Target size={15} className="text-violet-600 flex-shrink-0" />
                  <span className="font-bold text-slate-800 text-sm truncate">{m.name}</span>
                  {/* iter77ct.64: mostra nome da filial quando a meta esta vinculada a uma company especifica.
                      Meta global (sem company_id) NAO exibe badge (aplica-se a tudo). */}
                  {m.company_name && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700 border border-cyan-200 whitespace-nowrap truncate max-w-[140px]" data-testid={`dash-meta-company-${m.id}`}>
                      {m.company_name}
                    </span>
                  )}
                  <span className="text-[11px] text-slate-400 whitespace-nowrap hidden sm:inline">&middot; {fmtDateBR(m.start_date)} a {fmtDateBR(m.end_date)}</span>
                </div>
                <span className="font-black text-slate-900 text-sm flex-shrink-0">{(progress.percentual || 0).toFixed(1)}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden mb-0.5">
                <div className={`h-full bg-gradient-to-r ${pctColor} transition-all`} style={{ width: `${pct}%` }} />
              </div>
              <div className="flex items-center justify-between text-sm text-slate-500">
                <span className="sm:hidden text-[11px]">{fmtDateBR(m.start_date)} - {fmtDateBR(m.end_date)}</span>
                <span className="font-bold text-slate-800">{fmtMoney(progress.atual)}</span>
                <span>de {fmtMoney(m.value)}</span>
              </div>
              {/* iter156de: bloco de progresso por vendedor no dashboard principal.
                  Só aparece quando "Empresa + Vendedores" com metas individuais definidas. */}
              {hasVendorValues && Array.isArray(progress.by_vendor) && progress.by_vendor.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-100 space-y-1.5" data-testid={`dash-meta-vendors-${m.id}`}>
                  {progress.by_vendor.map(v => {
                    const vpct = Math.min(100, v.percentual || 0);
                    const vColor = vpct >= 100 ? 'from-emerald-500 to-green-600' : vpct >= 70 ? 'from-blue-500 to-violet-600' : vpct >= 40 ? 'from-amber-500 to-orange-500' : 'from-rose-500 to-red-600';
                    return (
                      <div key={v.vendedor_id} className="text-[11px]" data-testid={`dash-meta-vendor-${m.id}-${v.vendedor_id}`}>
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <span className="font-bold text-slate-700 truncate min-w-0">{v.name || 'Sem nome'}</span>
                          <span className="font-black text-slate-800 whitespace-nowrap">{fmtMoney(v.atual)} / {fmtMoney(v.meta || 0)} <span className="text-slate-500 font-semibold">({(v.percentual || 0).toFixed(1)}%)</span></span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                          <div className={`h-full bg-gradient-to-r ${vColor}`} style={{ width: `${vpct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MetasPage;
